/**
 * WordPress-SSO-Helper.
 *
 * Holt einen kurzlebigen SSO-Token vom Backend und schickt ihn an
 * dpconnect.de/wp-json/dp/v1/agent-sso — WP setzt dann den
 * wordpress_logged_in_*-Cookie und der Agent ist auch auf dpconnect.de
 * eingeloggt.
 *
 * Same-Site (.dpconnect.de) → Cookie wird vom Browser akzeptiert,
 * vorausgesetzt WP sendet `Access-Control-Allow-Credentials: true`.
 *
 * Failure-Mode: silent — wenn SSO fehlschlaegt (kein wc_user_id,
 * Token-Fehler, Netz-Fehler), wird nur console.warn geloggt, der
 * normale Agent-Portal-Login geht weiter.
 */
import { api } from "./api";

type SsoTokenResponse =
  | { enabled: false; reason: string }
  | {
      enabled: true;
      token: string;
      sso_url: string;
      wc_user_id: number;
      valid_seconds: number;
    };

const LS_LAST_SSO_AT = "dp_wp_sso_at";
const LS_LAST_SSO_RESULT = "dp_wp_sso_result";

/** Wie oft maximal pro Session ein SSO-Call gemacht wird (debounce, ms). */
const SSO_REFRESH_INTERVAL_MS = 30 * 60_000; // 30 Min

export type SsoResult =
  | { status: "ok"; wc_user_id: number; already: boolean }
  | { status: "skipped"; reason: string }
  | { status: "error"; reason: string };

/**
 * Triggert die WP-SSO einmalig.
 *
 * @param force  Wenn true, ignoriert den Debounce-Check und macht den Call sofort
 */
export async function triggerWpSso(force = false): Promise<SsoResult> {
  if (!force) {
    const lastAt = Number(localStorage.getItem(LS_LAST_SSO_AT) || "0");
    if (lastAt && Date.now() - lastAt < SSO_REFRESH_INTERVAL_MS) {
      const cached = localStorage.getItem(LS_LAST_SSO_RESULT);
      if (cached) {
        try {
          return JSON.parse(cached) as SsoResult;
        } catch {
          // ignore
        }
      }
    }
  }

  let tokenResp: SsoTokenResponse;
  try {
    tokenResp = await api.get<SsoTokenResponse>("/agent/sso-token");
  } catch (e) {
    const reason = e instanceof Error ? e.message : "token-fetch-failed";
    console.warn("[wp-sso] token holen fehlgeschlagen:", reason);
    return { status: "error", reason };
  }

  if (!tokenResp.enabled) {
    const result: SsoResult = { status: "skipped", reason: tokenResp.reason };
    persist(result);
    if (tokenResp.reason !== "no_wc_user_linked") {
      console.warn("[wp-sso] skip:", tokenResp.reason);
    }
    return result;
  }

  try {
    const r = await fetch(tokenResp.sso_url, {
      method: "GET",
      credentials: "include",
      mode: "cors",
      // Wichtig: keine custom headers — sonst CORS-Preflight + komplexer
      // Cookie ist Same-Site weil .dpconnect.de — funktioniert ohne SameSite=None
    });
    if (!r.ok) {
      const body = await r.text().catch(() => "");
      console.warn(`[wp-sso] WP returned ${r.status}:`, body.slice(0, 200));
      const result: SsoResult = {
        status: "error",
        reason: `wp-${r.status}`,
      };
      persist(result);
      return result;
    }
    const data = (await r.json().catch(() => ({}))) as {
      ok?: boolean;
      already?: boolean;
      user_id?: number;
    };
    if (!data.ok) {
      const result: SsoResult = { status: "error", reason: "wp-not-ok" };
      persist(result);
      return result;
    }
    const result: SsoResult = {
      status: "ok",
      wc_user_id: data.user_id || tokenResp.wc_user_id,
      already: !!data.already,
    };
    persist(result);
    return result;
  } catch (e) {
    const reason = e instanceof Error ? e.message : "fetch-failed";
    console.warn("[wp-sso] WP-Call fehlgeschlagen:", reason);
    return { status: "error", reason };
  }
}

/**
 * Expliziter SSO über Top-Level-Navigation: öffnet die SSO-URL in einem
 * neuen Tab (mit redirect auf /mein-konto/). Im Gegensatz zum stillen
 * fetch-SSO setzt so auch Safari die WordPress-Login-Cookies zuverlässig —
 * dort blockt ITP Cookies aus Cross-Origin-Hintergrund-Requests.
 *
 * Muss aus einem Klick-Handler aufgerufen werden (Popup-Blocker): das
 * Fenster wird sofort synchron geöffnet, das Ziel nach dem Token-Fetch
 * gesetzt.
 */
export async function openWpSsoWindow(): Promise<SsoResult> {
  const win = window.open("", "_blank");

  let tokenResp: SsoTokenResponse;
  let shopMode: { url: string } | null = null;
  try {
    // Shop-Modus-Token parallel holen: der Login-Klick aktiviert dann
    // automatisch auch den Teilen-Button auf dpconnect.de. Safari (ITP)
    // räumt das Shop-Modus-Cookie sonst still ab — so stellt jeder
    // Login-Klick beides in einem Rutsch wieder her.
    [tokenResp, shopMode] = await Promise.all([
      api.get<SsoTokenResponse>("/agent/sso-token"),
      api.get<{ url: string }>("/agent/shop-mode-link").catch(() => null),
    ]);
  } catch (e) {
    win?.close();
    const reason = e instanceof Error ? e.message : "token-fetch-failed";
    return { status: "error", reason };
  }

  if (!tokenResp.enabled) {
    win?.close();
    const result: SsoResult = { status: "skipped", reason: tokenResp.reason };
    persist(result);
    return result;
  }

  // Redirect-Ziel: mit Shop-Modus-Aktivierung (landet nach dem Cookie-Setzen
  // auf der Startseite, Teilen-Button aktiv) — sonst Fallback Mein Konto.
  let redirectPath = "/mein-konto/";
  if (shopMode?.url) {
    try {
      const u = new URL(shopMode.url);
      if (u.search.includes("_activate_agent_mode=")) redirectPath = `/${u.search}`;
    } catch {
      // Fallback bleibt /mein-konto/
    }
  }
  const url = `${tokenResp.sso_url}&redirect=${encodeURIComponent(redirectPath)}`;
  if (win) {
    win.location.href = url;
  } else {
    // Popup geblockt → im selben Tab navigieren
    window.location.assign(url);
  }
  const result: SsoResult = {
    status: "ok",
    wc_user_id: tokenResp.wc_user_id,
    already: false,
  };
  persist(result);
  return result;
}

function persist(result: SsoResult): void {
  try {
    localStorage.setItem(LS_LAST_SSO_AT, String(Date.now()));
    localStorage.setItem(LS_LAST_SSO_RESULT, JSON.stringify(result));
  } catch {
    // localStorage voll? ignorieren.
  }
}

/** Beim Logout aus dem Agenten-Portal aufrufen — leert nur unseren Cache,
 *  WP-Cookie bleibt aktiv (Wunsch des Users). */
export function clearWpSsoCache(): void {
  try {
    localStorage.removeItem(LS_LAST_SSO_AT);
    localStorage.removeItem(LS_LAST_SSO_RESULT);
  } catch {
    // ignore
  }
}

/** Letztes SSO-Ergebnis fuer UI-Anzeige (z.B. Status-Badge). */
export function getLastSsoResult(): SsoResult | null {
  const raw = localStorage.getItem(LS_LAST_SSO_RESULT);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as SsoResult;
  } catch {
    return null;
  }
}
